// The employee record as employee-master.html draws it: avatar header, eight
// tabs, Delete · Reset · Save on the bottom edge.
//
// The draft is one plain object (see employeeModel.js) because most of the
// record is editable grids — documents, pay components, targets, dependents,
// assets — which a flat form library handles badly. Save maps it to the
// PascalCase body in toPayload(); the endpoints are in
// docs/BACKEND-COMPLETE.md (Part 4.3).
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  AxAvatar,
  AxCaret,
  AxClearIcon,
  AxFact,
  AxField,
  AxPill,
  AxSectionHead,
  AxSwitchRow,
  AxTabs,
  AxTimelineRow,
} from "../../adminsitration/ui/adminUi";
import {
  addEmployee,
  createUserFromEmployee,
  deleteEmployeeDocumentFile,
  editEmployee,
  getEmployeeByGuid,
  uploadEmployeeDocument,
} from "../../../services/EmployeeApiServices";
import {
  LEAVE_REASONS,
  RANK,
  RENEWAL_STAGES,
  STATUSES,
  daysBetween,
  daysTo,
  docStatus,
  fmtDate,
  gratuity,
  ibanValid,
  iso,
  isLocalNationality,
  missingDocs,
  money,
  parseISO,
  pct,
  payOf,
  probationEnd,
  seedDocs,
  sortDocs,
  toDraft,
  toPayload,
  today,
  yearsBetween,
} from "./employeeModel";

const TABS = [
  { key: "profile", label: "Profile" },
  { key: "job", label: "Employment" },
  { key: "docs", label: "Documents" },
  { key: "pay", label: "Salary" },
  { key: "sales", label: "Sales role" },
  { key: "leave", label: "Leave & attendance" },
  { key: "assets", label: "Assets & access" },
  { key: "log", label: "Activity" },
];

const REQ = {
  profile: ["name", "code", "gender", "dob", "nationality", "phone1", "addr1", "emName", "emRel", "emPhone"],
  job: ["branch", "dept", "desig", "doj", "status"],
  pay: ["payFreq", "payMode", "payFrom"],
  sales: ["smCode", "smType"],
};

const LABEL = {
  name: "Name", code: "Employee code", gender: "Gender", dob: "Date of birth", nationality: "Nationality",
  phone1: "Mobile", addr1: "Present address", emName: "Emergency contact name",
  emRel: "Emergency contact relation", emPhone: "Emergency contact phone", branch: "Primary branch",
  dept: "Department", desig: "Designation", doj: "Date of joining", status: "Status",
  contractEnd: "Contract end", dol: "Date of leaving", payFreq: "Pay frequency", payMode: "Payment mode",
  payFrom: "Effective from", iban: "IBAN", smCode: "Salesman code", smType: "Salesman type",
};

const PER = { Monthly: "month", Fortnightly: "fortnight", Weekly: "week", Daily: "day", Hourly: "hour" };

const DelIcon = () => (
  <svg viewBox="0 0 14 14" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M2.6 3.8h8.8M5.4 3.8V2.6h3.2v1.2M4 3.8l.5 7.6h5l.5-7.6" />
  </svg>
);

// crude Latin -> Arabic letters, only a starting point for the WPS name
const AR = {
  th: "ث", kh: "خ", sh: "ش", a: "ا", b: "ب", t: "ت", j: "ج", h: "ح", d: "د", r: "ر", z: "ز", s: "س",
  f: "ف", q: "ق", k: "ك", l: "ل", m: "م", n: "ن", w: "و", y: "ي", i: "ي", e: "ي", o: "و", u: "و",
  g: "غ", p: "ب", v: "ف", c: "ك", x: "كس",
};
const toArabic = (name) => {
  const src = String(name || "").toLowerCase();
  let out = "";
  let i = 0;
  while (i < src.length) {
    const two = src.substr(i, 2);
    if (AR[two]) {
      out += AR[two];
      i += 2;
    } else {
      out += src[i] === " " ? " " : AR[src[i]] || "";
      i += 1;
    }
  }
  return out.trim();
};

const clone = (o) => JSON.parse(JSON.stringify(o));

const opts = (list) =>
  (list || []).map((o) => {
    const v = typeof o === "string" ? o : o.value;
    const l = typeof o === "string" ? o : o.label;
    return (
      <option key={v} value={v}>
        {l}
      </option>
    );
  });

/* ---------------------------------------------------------------- fields
   Declared out here, not inside EmployeeDetail: a component defined in a
   render is a new type every render, so React would remount the input and
   the caret would jump out of it on each keystroke. They read the draft and
   its setters from this context instead. */
const Ctx = createContext(null);
const useEd = () => useContext(Ctx);

const Sel = ({ f, label, req, list, placeholder, hint, disabled }) => {
  const { draft, set, errHint, errCls } = useEd();
  return (
    <AxField label={label} required={req} className="sel" hint={errHint(f) || hint} bad={!!errHint(f)}>
      <select value={draft[f] ?? ""} onChange={(e) => set(f, e.target.value)} className={errCls(f)} disabled={disabled}>
        {placeholder != null ? <option value="">{placeholder}</option> : null}
        {opts(list)}
      </select>
      <AxCaret />
    </AxField>
  );
};
const Txt = ({ f, label, req, hint, span, ...rest }) => {
  const { errHint, text } = useEd();
  return (
    <AxField label={label} required={req} span={span} hint={errHint(f) || hint} bad={!!errHint(f)}>
      {text(f, rest)}
    </AxField>
  );
};
const Dt = ({ f, label, req, hint }) => {
  const { errHint, date } = useEd();
  return (
    <AxField label={label} required={req} hint={errHint(f) || hint} bad={!!errHint(f)}>
      {date(f)}
    </AxField>
  );
};
const Fact = ({ l, v, s, tone }) => (
  <div className={"axa-fact" + (tone ? " axe-" + tone : "")}>
    <div className="k">{l}</div>
    <div className="v">{v}</div>
    {s ? <div className="axe-fs">{s}</div> : null}
  </div>
);
const RowDel = ({ list, i, onDone }) => {
  const { delRow } = useEd();
  return (
    <button
      type="button"
      className="axe-rowdel"
      aria-label="Remove"
      onClick={() => {
        delRow(list, i);
        if (onDone) onDone();
      }}
    >
      <DelIcon />
    </button>
  );
};

// branch chips with an add-popover; `star` marks the primary one
const ChipBox = ({ which, values, onRemove, onAdd, addLabel, star }) => {
  const { branchlist, branchName, pop, setPop, popRef, flagBranches } = useEd();
  const left = (branchlist || []).filter((b) => values.indexOf(b.guid) < 0);
  return (
    <div
      className={"axa-chipbox" + (which === "branch" && flagBranches ? " axe-errbox" : "")}
      ref={pop === which ? popRef : null}
    >
      {values.length ? (
        values.map((gd) => (
          <span key={gd} className={"axa-bchip" + (star === gd ? " def" : "")}>
            {branchName(gd)}
            {star === gd ? <span className="star">primary</span> : null}
            <button type="button" aria-label={"Remove " + branchName(gd)} onClick={() => onRemove(gd)}>
              <AxClearIcon />
            </button>
          </span>
        ))
      ) : (
        <span className="axa-chipempty">None yet.</span>
      )}
      <button
        type="button"
        className="axa-chipadd"
        onClick={(e) => {
          e.stopPropagation();
          setPop((cur) => (cur === which ? null : which));
        }}
      >
        {addLabel}
      </button>
      {pop === which && (
        <div className="axa-chippop">
          {left.length ? (
            left.map((b) => (
              <label key={b.guid}>
                <input type="checkbox" checked={false} onChange={() => onAdd(b.guid)} />
                {b.name}
              </label>
            ))
          ) : (
            <span className="axa-chipempty">Nothing left to add</span>
          )}
        </div>
      )}
    </div>
  );
};

const EmployeeDetail = ({
  record, // draft-shaped (toDraft) record, or a blank one for New
  isNew,
  lookups: lk,
  branchlist,
  branch,
  userId,
  employees,
  canSave = true,
  canDelete,
  onSaved,
  onClose,
  onDelete,
  initialTab = "profile",
}) => {
  const [draft, setDraft] = useState(() => clone(record));
  const [original, setOriginal] = useState(() => JSON.stringify(record));
  const [tab, setTab] = useState(initialTab);
  const [showErr, setShowErr] = useState(false);
  const [msg, setMsg] = useState("");
  const [activeDoc, setActiveDoc] = useState(0);
  const [pop, setPop] = useState(null); // "branch" | "sell"
  const [saving, setSaving] = useState(false);
  const [over, setOver] = useState(false);
  const fileRef = useRef(null);
  const popRef = useRef(null);
  const nameRef = useRef(null);
  const draftRef = useRef(draft);
  const originalRef = useRef(original);
  draftRef.current = draft;
  originalRef.current = original;

  useEffect(() => {
    if (isNew && nameRef.current) nameRef.current.focus();
  }, [isNew]);

  // The list row is enough to show the record at once; GetByGuid adds the
  // change history and file links. If the user has already started editing,
  // only the history is merged so their typing is not thrown away.
  useEffect(() => {
    if (isNew || !record.guid) return undefined;
    let live = true;
    getEmployeeByGuid(record.guid)
      .then((r) => {
        if (!live || !r || typeof r !== "object") return;
        const full = toDraft(r, lk);
        const untouched = JSON.stringify(draftRef.current) === originalRef.current;
        setDraft(untouched ? clone(full) : { ...draftRef.current, history: full.history });
        if (untouched) setOriginal(JSON.stringify(full));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.guid, isNew]);

  useEffect(() => {
    if (!pop) return undefined;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setPop(null);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [pop]);

  const dirty = JSON.stringify(draft) !== original;

  const branchName = useMemo(() => {
    const m = {};
    (branchlist || []).forEach((b) => {
      m[b.guid] = b.name;
    });
    return (g) => m[g] || g || "—";
  }, [branchlist]);

  /* ---------------------------------------------------- editing */
  const touch = () => setMsg("");
  const patch = (obj) => {
    setDraft((d) => ({ ...d, ...obj }));
    touch();
  };
  const set = (f, v) => {
    setDraft((d) => {
      const next = { ...d, [f]: v };
      if (f === "nationality") {
        next.empType = isLocalNationality(lk, v) ? "Local" : "Expatriate";
        next.docs = seedDocs(d.docs, next.empType, lk);
      }
      if (f === "empType") next.docs = seedDocs(d.docs, v, lk);
      if (f === "branch" && v && next.branches.indexOf(v) < 0) next.branches = [v, ...next.branches];
      if (f === "isSalesman" && v) {
        if (!next.smCode) next.smCode = "SM" + String(next.code || "").replace(/\D/g, "").slice(-4);
        if (!(next.sellFrom || []).length && next.branch) next.sellFrom = [next.branch];
      }
      if (f === "status" && v === "Left" && !next.dol) next.dol = iso(today());
      return next;
    });
    touch();
  };
  const setRow = (list, i, f, v) => {
    setDraft((d) => {
      const rows = d[list].slice();
      rows[i] = { ...rows[i], [f]: v };
      if (list === "docs") {
        const t = lk.docBy[rows[i].type];
        if ((f === "issue" || f === "type") && t && t.expires && rows[i].issue && !rows[i].expiry) {
          const exp = parseISO(rows[i].issue);
          exp.setMonth(exp.getMonth() + t.validityMonths);
          rows[i].expiry = iso(exp);
        }
      }
      return { ...d, [list]: rows };
    });
    touch();
  };
  const addRow = (list, row) => {
    setDraft((d) => ({ ...d, [list]: [...(d[list] || []), row] }));
    touch();
  };
  const delRow = (list, i) => {
    setDraft((d) => ({ ...d, [list]: d[list].filter((_, k) => k !== i) }));
    touch();
  };

  /* ---------------------------------------------------- validation */
  const check = useMemo(() => {
    const bad = [];
    const tabBad = {};
    const flag = (f, t, m) => {
      bad.push({ f, tab: t, msg: m });
      tabBad[t] = "bad";
    };
    Object.keys(REQ).forEach((t) => {
      if (t === "sales" && !draft.isSalesman) return;
      REQ[t].forEach((f) => {
        const v = draft[f];
        if (v === "" || v == null) flag(f, t);
      });
    });
    if (!(draft.branches || []).length) flag("branches", "job", "Add at least one branch");
    if (draft.contract === "Fixed term" && !draft.contractEnd) flag("contractEnd", "job");
    if (draft.status === "Left" && !draft.dol) flag("dol", "job");
    if (draft.payMode === "Bank transfer (WPS)" && !draft.wpsExempt) {
      if (!draft.iban) flag("iban", "pay");
      else if (ibanValid(draft.iban) === false) flag("iban", "pay", "IBAN fails the checksum");
    }
    if (draft.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email))
      flag("email", "profile", "Work email is not valid");
    const code = (draft.code || "").trim().toLowerCase();
    const dup = code && (employees || []).find((x) => x.guid !== draft.guid && (x.code || "").toLowerCase() === code);
    if (dup) flag("code", "profile", "Code " + draft.code + " already belongs to " + dup.name);
    if (draft.isSalesman && draft.smCode) {
      const sdup = (employees || []).find(
        (x) => x.guid !== draft.guid && x.isSalesman && x.smCode && x.smCode === draft.smCode
      );
      if (sdup) flag("smCode", "sales", "Salesman code is already used by " + sdup.name);
    }
    if (!(draft.comps || []).some((c) => c.c === "BASIC" && +c.amt > 0))
      flag("comps", "pay", "Basic salary must be more than zero");
    if (missingDocs(draft, lk).length && !tabBad.docs) tabBad.docs = "warn";
    if ((draft.docs || []).some((d) => RANK[docStatus(d, lk).k] <= 2) && !tabBad.docs) tabBad.docs = "warn";
    return { bad, tabBad, has: (f) => bad.some((b) => b.f === f) };
  }, [draft, employees, lk]);

  const errCls = (f, extra = "") => (extra ? extra + " " : "") + (showErr && check.has(f) ? "err" : "");
  const errHint = (f) => {
    if (!showErr) return undefined;
    const b = check.bad.find((x) => x.f === f);
    return b ? b.msg || LABEL[f] + " is required" : undefined;
  };

  /* ---------------------------------------------------- save */
  const save = async () => {
    if (saving) return;
    if (check.bad.length) {
      setShowErr(true);
      const first = check.bad[0];
      setTab(first.tab);
      setMsg(first.msg || (LABEL[first.f] || first.f) + " is required");
      toast.error("Some required fields are still empty");
      return;
    }
    setSaving(true);
    try {
      const body = toPayload(draft, { branch, userId, isNew });
      const res = isNew ? await addEmployee(body) : await editEmployee(body);
      const data = res?.data;
      if (data === false) throw new Error("The server refused the save");
      toast.success(isNew ? draft.name + " added" : "Saved");
      setShowErr(false);
      setMsg("");
      setOriginal(JSON.stringify(draft));
      onSaved && onSaved(data && typeof data === "object" ? data : null, draft);
    } catch (error) {
      console.error("Error saving employee:", error);
      const m = error?.response?.data?.Message || error?.response?.data?.message || error.message;
      setMsg(error?.response?.status === 404 ? "The Employee API is not on this server yet" : m || "Save failed");
      toast.error(m || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const reset = () => {
    setDraft(JSON.parse(original));
    setShowErr(false);
    setMsg("");
    toast(isNew ? "Form cleared" : "Changes discarded");
  };

  // F9 saves, the same key the terminal screens use
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F9") {
        e.preventDefault();
        if (canSave) saveRef.current();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [canSave]);

  /* ---------------------------------------------------- files */
  const doc = (draft.docs || [])[activeDoc];
  const pickFile = () => {
    if (!doc) return toast.error("Pick a document row first");
    if (!draft.guid || !doc.guid) return toast.error("Save the employee first, then attach scans");
    fileRef.current && fileRef.current.click();
  };
  const upload = async (file) => {
    if (!file || !doc) return;
    if (!draft.guid || !doc.guid) return toast.error("Save the employee first, then attach scans");
    if (file.size > 10 * 1024 * 1024) return toast.error("That file is over 10 MB");
    const fd = new FormData();
    fd.append("employeeGuid", draft.guid);
    fd.append("documentGuid", doc.guid);
    fd.append("documentType", doc.type);
    fd.append("file", file);
    try {
      const f = await uploadEmployeeDocument(fd);
      const row = {
        guid: f?.guid || "",
        n: f?.fileName || file.name,
        sz: f?.sizeKb || Math.round(file.size / 1024),
        on: iso(today()),
        url: f?.url || "",
      };
      const i = activeDoc;
      setDraft((d) => {
        const docs = d.docs.slice();
        docs[i] = { ...docs[i], files: [...(docs[i].files || []), row] };
        return { ...d, docs };
      });
      // the file is already stored; keep the saved copy in step so it does not read as unsaved
      setOriginal((o) => {
        const d = JSON.parse(o);
        if (d.docs && d.docs[i]) d.docs[i].files = [...(d.docs[i].files || []), row];
        return JSON.stringify(d);
      });
      toast.success("Scan attached");
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Upload failed");
    }
  };
  const removeFile = async (k) => {
    const f = doc?.files?.[k];
    if (!f) return;
    try {
      if (f.guid) await deleteEmployeeDocumentFile(f.guid);
    } catch (error) {
      console.error("Delete file failed:", error);
      return toast.error("Could not remove the file");
    }
    const i = activeDoc;
    const drop = (d) => {
      const docs = d.docs.slice();
      docs[i] = { ...docs[i], files: docs[i].files.filter((_, x) => x !== k) };
      return { ...d, docs };
    };
    setDraft(drop);
    setOriginal((o) => JSON.stringify(drop(JSON.parse(o))));
  };

  /* ---------------------------------------------------- ERP user */
  const createUser = async () => {
    if (draft.userName) return toast("Already linked to " + draft.userName);
    if (!draft.guid) return toast.error("Save the employee first");
    try {
      const u = await createUserFromEmployee({ EmployeeGuid: draft.guid, Branch: branch, createdUser: `{${userId?.id}}` });
      if (!u) throw new Error("no user returned");
      const upd = { userGuid: u.guid || "", userName: u.userName || u.name || "" };
      patch(upd);
      setOriginal((o) => JSON.stringify({ ...JSON.parse(o), ...upd }));
      toast.success("ERP user created with this person's name, code and branches");
    } catch (error) {
      console.error("Create user failed:", error);
      toast.error("Could not create the user");
    }
  };

  /* ---------------------------------------------------- derived */
  const p = payOf(draft, lk);
  const g = gratuity(draft, lk);
  const svc = draft.doj ? Math.max(0, yearsBetween(parseISO(draft.doj), today())) : 0;
  const miss = missingDocs(draft, lk);
  const sorted = sortDocs(draft.docs, lk);
  const health = { expired: 0, critical: 0, warning: 0, watch: 0, valid: 0 };
  (draft.docs || []).forEach((d) => {
    health[docStatus(d, lk).k] += 1;
  });
  const probEnd = probationEnd(draft);
  const probLeft = probEnd ? daysBetween(today(), probEnd) : null;
  const iban = ibanValid(draft.iban);
  const CUR = lk.currency;
  const managers = (employees || []).filter((x) => x.guid !== draft.guid && x.status !== "Left");

  const tabs = TABS.map((t) => ({
    ...t,
    count: t.key === "docs" ? (draft.docs || []).length : undefined,
    warn: !!check.tabBad[t.key] && (showErr || check.tabBad[t.key] === "warn"),
  }));

  const sub = [
    draft.desig || "No designation",
    draft.code || "no code",
    branchName(draft.branch),
    draft.empType,
    svc > 0.05 ? svc.toFixed(1) + " yrs service" : "joining",
  ].join(" · ");

  /* ---------------------------------------------------- small builders */
  const text = (f, props = {}) => (
    <input
      type="text"
      autoComplete="off"
      value={draft[f] ?? ""}
      onChange={(e) => set(f, e.target.value)}
      {...props}
      className={errCls(f, props.className)}
    />
  );
  const num = (f, props = {}) => (
    <input
      type="number"
      value={draft[f] ?? ""}
      onChange={(e) => set(f, e.target.value === "" ? "" : +e.target.value)}
      {...props}
      className={errCls(f, "mono")}
    />
  );
  const date = (f) => (
    <input type="date" value={draft[f] || ""} onChange={(e) => set(f, e.target.value)} className={errCls(f)} />
  );
  const ctx = {
    draft, set, errHint, errCls, text, date, delRow, branchlist, branchName,
    pop, setPop, popRef, flagBranches: showErr && check.has("branches"),
  };
  const cellSel = (list, i, f, choices) => (
    <select value={draft[list][i][f] ?? ""} onChange={(e) => setRow(list, i, f, e.target.value)}>
      {opts(choices)}
    </select>
  );
  const cellIn = (list, i, f, props = {}) => (
    <input
      value={draft[list][i][f] ?? ""}
      onChange={(e) =>
        setRow(list, i, f, props.type === "number" ? (e.target.value === "" ? 0 : +e.target.value) : e.target.value)
      }
      {...props}
    />
  );

  /* ================================================================ render */
  return (
    <Ctx.Provider value={ctx}>
    <form
      className="axa-detailform axe-detail"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      {/* ============ HEADER ============ */}
      <div className="axa-dhead">
        <AxAvatar name={draft.name || "new"} />
        <div className="axa-dtitle">
          <div className="t">
            <span className="nm">{draft.name || "New employee"}</span>
          </div>
          <div className="s">
            {sub}
            {draft.isSalesman ? <span className="axa-tag axe-sales">Salesman {draft.smCode}</span> : null}
          </div>
        </div>
        <AxPill state={isNew ? "new" : dirty ? "dirty" : "saved"} />
      </div>

      <AxTabs tabs={tabs} active={tab} onChange={setTab} />

      <div className="axa-tabbody">
        {/* ---------------------------------------------------- PROFILE */}
        <div className={"axa-pane" + (tab === "profile" ? " show" : "")}>
          <AxSectionHead>Identity</AxSectionHead>
          <div className="axa-formgrid">
            <AxField label="Full name (as in passport)" required hint={errHint("name")} bad={!!errHint("name")}>
              {text("name", { placeholder: "Full name", ref: nameRef })}
            </AxField>
            <AxField label="Name in Arabic" hint="Needed by some banks for the WPS file.">
              <div className="axe-withbtn">
                <input
                  type="text"
                  dir="rtl"
                  placeholder="الاسم"
                  value={draft.arName}
                  onChange={(e) => set("arName", e.target.value)}
                />
                <button
                  type="button"
                  className="axa-btn axa-btn-ghost"
                  onClick={() => {
                    set("arName", toArabic(draft.name));
                    toast("Transliterated — check it before saving");
                  }}
                >
                  Convert
                </button>
              </div>
            </AxField>
            <Txt f="code" label="Employee code" req placeholder="e.g. EMP1042" className="mono" />
            <Txt f="shortName" label="Known as" placeholder="Shown on invoices" />
            <Sel f="gender" label="Gender" req list={["Male", "Female"]} placeholder="Select" />
            <Dt
              f="dob"
              label="Date of birth"
              req
              hint={draft.dob ? Math.floor(yearsBetween(parseISO(draft.dob), today())) + " years old" : undefined}
            />
            <Sel
              f="nationality"
              label="Nationality"
              req
              list={lk.nationalities}
              placeholder="Select nationality"
              hint="Decides which documents this person must have."
            />
            <Sel f="empType" label="Employment type" req list={["Local", "Expatriate"]} />
            <Sel f="marital" label="Marital status" list={["Single", "Married", "Other"]} />
          </div>

          <AxSectionHead>Contact</AxSectionHead>
          <div className="axa-formgrid">
            <Txt f="phone1" label="Mobile" req className="mono" inputMode="tel" placeholder="+974 …" />
            <Txt f="phone2" label="Home-country number" className="mono" inputMode="tel" placeholder="Optional" />
            <Txt f="email" label="Work email" placeholder="name@company.com" />
            <Txt f="addr1" label="Present address" req span={2} placeholder="Building, street, area" />
            <Sel f="accom" label="Accommodation" list={["Company provided", "Own", "Allowance paid"]} />
            <Txt f="homeAddr" label="Permanent / home-country address" span={2} placeholder="Optional" />
            <Sel f="blood" label="Blood group" list={["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"]} placeholder="—" />
          </div>

          <AxSectionHead>
            Emergency contact <span className="req">*</span>
          </AxSectionHead>
          <div className="axa-formgrid">
            <Txt f="emName" label="Name" />
            <Txt f="emRel" label="Relation" placeholder="Brother, spouse…" />
            <Txt f="emPhone" label="Phone" className="mono" inputMode="tel" />
          </div>

          <AxSectionHead>
            Dependents under company sponsorship
            <button
              type="button"
              className="axe-hintbtn"
              onClick={() => addRow("deps", { guid: "", n: "", rel: "Spouse", dob: "", spon: true, qid: "", exp: "" })}
            >
              + Add dependent
            </button>
          </AxSectionHead>
          {(draft.deps || []).length ? (
            <table className="axe-gtable">
              <thead>
                <tr>
                  <th>Name</th>
                  <th style={{ width: 120 }}>Relation</th>
                  <th style={{ width: 140 }}>Date of birth</th>
                  <th style={{ width: 150 }}>ID number</th>
                  <th style={{ width: 150 }}>ID expiry</th>
                  <th style={{ width: 34 }} />
                </tr>
              </thead>
              <tbody>
                {draft.deps.map((d, i) => {
                  const left = daysTo(d.exp);
                  const k = left === null ? "watch" : left < 0 ? "expired" : left <= 30 ? "warning" : left <= 90 ? "watch" : "valid";
                  return (
                    <tr key={d.guid || "n" + i}>
                      <td>{cellIn("deps", i, "n")}</td>
                      <td>{cellSel("deps", i, "rel", ["Spouse", "Son", "Daughter", "Parent"])}</td>
                      <td>{cellIn("deps", i, "dob", { type: "date" })}</td>
                      <td>{cellIn("deps", i, "qid", { className: "mono" })}</td>
                      <td>
                        <div className="axe-cellstack">
                          {cellIn("deps", i, "exp", { type: "date" })}
                          {d.exp ? <span className={"axe-st " + k}>{left < 0 ? "Expired" : left + "d"}</span> : null}
                        </div>
                      </td>
                      <td>
                        <RowDel list="deps" i={i} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="axa-emptyline">No dependents under company sponsorship.</div>
          )}
        </div>

        {/* ---------------------------------------------------- EMPLOYMENT */}
        <div className={"axa-pane" + (tab === "job" ? " show" : "")}>
          <AxSectionHead>Posting</AxSectionHead>
          <div className="axa-formgrid">
            <Sel
              f="branch"
              label="Primary branch"
              req
              list={(branchlist || []).map((b) => ({ value: b.guid, label: b.name }))}
              placeholder="Select branch"
            />
            <Sel f="dept" label="Department" req list={lk.departments} placeholder="Select department" />
            <Sel f="desig" label="Designation" req list={lk.designations} placeholder="Select designation" />
            <Sel f="grade" label="Grade" list={lk.grades} placeholder="—" />
            <Sel
              f="reportsTo"
              label="Reports to"
              list={managers.map((x) => ({ value: x.guid, label: x.name + " · " + (x.desig || "—") }))}
              placeholder="Nobody"
            />
            <Sel f="workLoc" label="Work location" list={["Branch", "Warehouse", "Field", "Head office"]} />
          </div>

          <AxSectionHead>
            Branches this employee may work in <span className="req">*</span>
          </AxSectionHead>
          <ChipBox
            which="branch"
            values={draft.branches || []}
            star={draft.branch}
            addLabel="+ Add branch"
            onAdd={(gd) => patch({ branches: [...draft.branches, gd], branch: draft.branch || gd })}
            onRemove={(gd) => {
              if (draft.branches.length === 1) return toast.error("Keep at least one branch");
              const next = draft.branches.filter((x) => x !== gd);
              patch({ branches: next, branch: draft.branch === gd ? next[0] : draft.branch });
            }}
          />
          {showErr && check.has("branches") ? <div className="axe-errline">Add at least one branch.</div> : null}

          <AxSectionHead>Contract</AxSectionHead>
          <div className="axa-formgrid">
            <Dt f="doj" label="Date of joining" req hint={draft.doj ? svc.toFixed(1) + " years of service" : undefined} />
            <Sel f="contract" label="Contract type" req list={["Unlimited", "Fixed term"]} />
            <Dt
              f="contractEnd"
              label="Contract end"
              hint={draft.contract === "Fixed term" ? "Required — generates an expiry reminder." : "Not needed for an unlimited contract."}
            />
            <AxField
              label="Probation (months)"
              hint={
                probEnd
                  ? probLeft > 0
                    ? "Confirmation due " + fmtDate(iso(probEnd)) + " (" + probLeft + " days)"
                    : "Confirmation was due " + fmtDate(iso(probEnd))
                  : undefined
              }
              bad={probLeft != null && probLeft > 0 && probLeft <= 15}
            >
              {num("probation", { min: 0, max: 6 })}
            </AxField>
            <Sel f="status" label="Status" req list={STATUSES} />
            <div className={draft.status === "Left" ? "" : "axe-dim"}>
              <Dt f="dol" label="Date of leaving" hint="Freezes payroll and signs the user out everywhere." />
            </div>
            <div className={draft.status === "Left" ? "" : "axe-dim"}>
              <Sel f="leaveReason" label="Reason for leaving" list={LEAVE_REASONS} placeholder="—" hint="Changes the gratuity entitlement." />
            </div>
          </div>

          <AxSectionHead>Job history</AxSectionHead>
          <div className="axa-timeline">
            {draft.doj ? (
              <AxTimelineRow
                tone="ok"
                title={"Joined as " + (draft.desig || "—")}
                sub={branchName(draft.branch) + " · " + (draft.dept || "—")}
                when={fmtDate(draft.doj)}
              />
            ) : null}
            {probEnd ? (
              <AxTimelineRow
                tone={probLeft > 0 ? "warn" : ""}
                title="Probation ends"
                sub={draft.probation + " months from joining"}
                when={fmtDate(iso(probEnd))}
              />
            ) : null}
            {(draft.history || [])
              .filter((h) => /designation|branch|grade|status|transfer/i.test(h.t))
              .map((h, i) => (
                <AxTimelineRow key={i} title={h.t} sub={h.s} when={fmtDate(h.d)} />
              ))}
            {draft.dol ? (
              <AxTimelineRow
                tone="bad"
                title={"Left — " + (draft.leaveReason || "not stated")}
                sub="payroll frozen, access revoked"
                when={fmtDate(draft.dol)}
              />
            ) : null}
            {!draft.doj ? <div className="axa-emptyline">Nothing recorded yet.</div> : null}
          </div>
        </div>

        {/* ---------------------------------------------------- DOCUMENTS */}
        <div className={"axa-pane" + (tab === "docs" ? " show" : "")}>
          <div className="axe-health">
            {[
              { k: "expired", l: "Expired", s: "fix today" },
              { k: "critical", l: "≤ 15 days", s: "start the renewal" },
              { k: "warning", l: "≤ 30 days", s: "reminder due" },
              { k: "watch", l: "≤ 90 days", s: "or no date yet" },
              { k: "valid", l: "In order", s: miss.length ? miss.length + " still missing" : "nothing due" },
            ].map((c) => (
              <div key={c.k} className={"axe-hcell " + c.k}>
                <div className="l">{c.l}</div>
                <div className="v">{health[c.k]}</div>
                <div className="s">{c.s}</div>
              </div>
            ))}
          </div>

          <AxSectionHead>
            Documents on file
            <button
              type="button"
              className="axe-hintbtn"
              onClick={() => {
                addRow("docs", { guid: "", type: lk.documentTypes[0]?.code || "", no: "", issue: "", expiry: "", stage: "Not started", files: [], remarks: "" });
                setActiveDoc((draft.docs || []).length);
              }}
            >
              + Add document
            </button>
          </AxSectionHead>

          {miss.length ? (
            <div className="axe-missing">
              Required for {draft.empType === "Local" ? "a local" : "an expatriate"} employee and not on file:{" "}
              {miss.map((t) => t.name).join(", ")}
              <button
                type="button"
                onClick={() => patch({ docs: seedDocs(draft.docs, draft.empType, lk) })}
              >
                Add them
              </button>
            </div>
          ) : null}

          <div className="axe-gscroll">
            <table className="axe-gtable">
              <thead>
                <tr>
                  <th style={{ width: 190 }}>Document</th>
                  <th style={{ width: 130 }}>Number</th>
                  <th style={{ width: 128 }}>Issued</th>
                  <th style={{ width: 128 }}>Expires</th>
                  <th style={{ width: 118 }}>Status</th>
                  <th style={{ width: 150 }}>Renewal</th>
                  <th style={{ width: 64 }}>Files</th>
                  <th style={{ width: 34 }} />
                </tr>
              </thead>
              <tbody>
                {!sorted.length ? (
                  <tr>
                    <td colSpan={8} className="axe-emptycell">
                      Nothing on file yet. Pick a nationality on the Profile tab and the required papers appear here.
                    </td>
                  </tr>
                ) : (
                  sorted.map(({ d, i, st }) => {
                    const t = lk.docBy[d.type];
                    return (
                      <tr key={d.guid || "n" + i} className={i === activeDoc ? "on" : ""}>
                        <td>
                          {cellSel(
                            "docs",
                            i,
                            "type",
                            lk.documentTypes.map((x) => ({ value: x.code, label: x.name }))
                          )}
                        </td>
                        <td>{cellIn("docs", i, "no", { className: "mono" })}</td>
                        <td>{cellIn("docs", i, "issue", { type: "date" })}</td>
                        <td>
                          {t && t.expires ? cellIn("docs", i, "expiry", { type: "date" }) : <span className="axe-muted">never</span>}
                        </td>
                        <td>
                          <span className={"axe-st " + st.k}>{st.lbl}</span>
                        </td>
                        <td>{t && t.expires ? cellSel("docs", i, "stage", RENEWAL_STAGES) : <span className="axe-muted">—</span>}</td>
                        <td>
                          <button type="button" className="axe-st stage" onClick={() => setActiveDoc(i)}>
                            {(d.files || []).length} file{(d.files || []).length === 1 ? "" : "s"}
                          </button>
                        </td>
                        <td>
                          <RowDel list="docs" i={i} onDone={() => setActiveDoc(0)} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <AxSectionHead>
            Attachments for&nbsp;<span className="axe-plain">{doc ? lk.docBy[doc.type]?.name || doc.type : "—"}</span>
          </AxSectionHead>
          <div
            className={"axe-drop" + (over ? " over" : "")}
            onDragEnter={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              const f = e.dataTransfer?.files?.[0];
              if (f) upload(f);
            }}
          >
            <span className="ic">
              <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M8 11V3.4M8 3.4 5.2 6.2M8 3.4l2.8 2.8M2.4 10.8v1.8a1.4 1.4 0 0 0 1.4 1.4h8.4a1.4 1.4 0 0 0 1.4-1.4v-1.8" />
              </svg>
            </span>
            <div>
              <div className="t">
                Drop a scan here, or{" "}
                <button type="button" className="axe-link" onClick={pickFile}>
                  browse
                </button>
              </div>
              <div className="s">PDF, JPG, PNG or HEIC up to 10 MB. Re-uploading keeps the previous copy.</div>
            </div>
            <input
              ref={fileRef}
              type="file"
              hidden
              accept=".pdf,.jpg,.jpeg,.png,.heic"
              onChange={(e) => {
                upload(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </div>
          <div className="axe-files">
            {doc && (doc.files || []).length ? (
              doc.files.map((f, k) => (
                <div className="axe-file" key={f.guid || k}>
                  <a className="thumb" href={f.url || undefined} target="_blank" rel="noreferrer">
                    <svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.4">
                      <path d="M5 2h6l4 4v12H5z" />
                      <path d="M11 2v4h4M7.4 10.5h5.2M7.4 13.4h5.2" />
                    </svg>
                  </a>
                  <div className="fn" title={f.n}>
                    {f.n}
                  </div>
                  <div className="fm">
                    <span>{f.sz} KB</span>
                    <button type="button" aria-label="Remove file" onClick={() => removeFile(k)}>
                      <DelIcon />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="axa-emptyline">No scan uploaded for this document yet.</div>
            )}
          </div>
        </div>

        {/* ---------------------------------------------------- SALARY */}
        <div className={"axa-pane" + (tab === "pay" ? " show" : "")}>
          <AxSectionHead>Pay structure</AxSectionHead>
          <div className="axa-formgrid">
            <Sel f="payFreq" label="Pay frequency" req list={["Monthly", "Fortnightly", "Weekly", "Daily", "Hourly"]} />
            <Sel f="payMode" label="Payment mode" req list={["Bank transfer (WPS)", "Cash", "Cheque"]} />
            <Dt f="payFrom" label="Effective from" req hint="A change writes a new revision, it does not overwrite the old one." />
          </div>

          <div className="axe-gscroll" style={{ marginTop: 13 }}>
            <table className="axe-gtable">
              <thead>
                <tr>
                  <th style={{ width: 220 }}>Component</th>
                  <th style={{ width: 120 }}>Type</th>
                  <th className="num" style={{ width: 120 }}>
                    Amount
                  </th>
                  <th style={{ width: 110 }}>Gratuity base</th>
                  <th style={{ width: 120 }}>Insurance base</th>
                  <th style={{ width: 34 }} />
                </tr>
              </thead>
              <tbody>
                {(draft.comps || []).map((x, i) => {
                  const c = lk.compBy[x.c] || {};
                  return (
                    <tr key={i}>
                      <td>{cellSel("comps", i, "c", lk.salaryComponents.map((k) => ({ value: k.code, label: k.name })))}</td>
                      <td className="axe-muted">{c.kind || ""}</td>
                      <td className="num">{cellIn("comps", i, "amt", { type: "number", className: "num mono" })}</td>
                      <td className="axe-muted">{c.gratuity ? "Included" : "—"}</td>
                      <td className="axe-muted">{c.insurance ? "Included" : "—"}</td>
                      <td>
                        <RowDel list="comps" i={i} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button type="button" className="axe-addrow" onClick={() => addRow("comps", { c: "OTHER", amt: 0 })}>
            + Add component
          </button>
          {showErr && check.has("comps") ? <div className="axe-errline">Basic salary must be more than zero.</div> : null}

          <div className="axe-paysum">
            {[
              { l: "Gross earnings", v: p.gross, s: "before deductions" },
              { l: "Deductions", v: p.ded, s: "insurance, loans" },
              { l: "Net pay", v: p.net, s: "per " + (PER[draft.payFreq] || "month"), cls: "net" },
            ].map((c) => (
              <div key={c.l} className={"axe-pcell " + (c.cls || "")}>
                <div className="l">{c.l}</div>
                <div className="v">
                  <span className="cur">{CUR}</span>
                  {money(c.v)}
                </div>
                <div className="s">{c.s}</div>
              </div>
            ))}
          </div>

          <AxSectionHead>Bank &amp; wage protection</AxSectionHead>
          <div className="axa-formgrid">
            <Sel f="bank" label="Bank" list={lk.banks} placeholder="—" />
            <AxField
              label="IBAN"
              required={draft.payMode === "Bank transfer (WPS)" && !draft.wpsExempt}
              hint={
                errHint("iban") ||
                (iban === false
                  ? "Fails the IBAN checksum — check the digits."
                  : iban
                  ? "Checksum OK."
                  : draft.payMode === "Bank transfer (WPS)"
                  ? "Required for the WPS file."
                  : "Not needed for this payment mode.")
              }
              bad={!!errHint("iban") || iban === false}
            >
              {text("iban", { className: "mono", placeholder: "QA00 XXXX 0000 0000 0000 0000 000" })}
            </AxField>
            <Txt f="bankShort" label="Bank short name" className="mono" placeholder="Required by the SIF" />
            <Txt f="gosi" label="Social insurance no." className="mono" placeholder="GOSI / Ministry no." />
            <AxSwitchRow label="Exempt from WPS" checked={!!draft.wpsExempt} onChange={(e) => set("wpsExempt", e.target.checked)} />
            <AxSwitchRow label="Hold salary" checked={!!draft.salaryHold} onChange={(e) => set("salaryHold", e.target.checked)} />
          </div>

          <AxSectionHead>End of service &amp; entitlements</AxSectionHead>
          <div className="axa-facts">
            <Fact l="Service length" v={svc.toFixed(1) + " yrs"} s={"since " + fmtDate(draft.doj)} />
            <Fact l="Gratuity base" v={CUR + " " + money(p.gratBase || p.basic)} s="basic only" />
            <Fact l="Accrued end of service" v={CUR + " " + money(g.amount)} s={g.note} tone="good" />
            <Fact l="Cost to company" v={CUR + " " + money(p.ctc)} s="incl. employer cost" />
          </div>

          <AxSectionHead>
            Loans &amp; advances
            <button
              type="button"
              className="axe-hintbtn"
              onClick={() => addRow("loans", { guid: "", on: iso(today()), t: "Salary advance", amt: 0, inst: 0, out: 0 })}
            >
              + Add
            </button>
          </AxSectionHead>
          {(draft.loans || []).length ? (
            <table className="axe-gtable">
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Date</th>
                  <th>Type</th>
                  <th className="num" style={{ width: 110 }}>
                    Principal
                  </th>
                  <th className="num" style={{ width: 110 }}>
                    Instalment
                  </th>
                  <th className="num" style={{ width: 110 }}>
                    Outstanding
                  </th>
                  <th style={{ width: 34 }} />
                </tr>
              </thead>
              <tbody>
                {draft.loans.map((l, i) => (
                  <tr key={l.guid || "n" + i}>
                    <td>{cellIn("loans", i, "on", { type: "date" })}</td>
                    <td>{cellSel("loans", i, "t", ["Salary advance", "Personal loan", "Visa / ticket advance"])}</td>
                    <td className="num">{cellIn("loans", i, "amt", { type: "number", className: "num mono" })}</td>
                    <td className="num">{cellIn("loans", i, "inst", { type: "number", className: "num mono" })}</td>
                    <td className="num">{cellIn("loans", i, "out", { type: "number", className: "num mono axe-warntext" })}</td>
                    <td>
                      <RowDel list="loans" i={i} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="axa-emptyline">No loan or advance outstanding.</div>
          )}

          <AxSectionHead>Revision history</AxSectionHead>
          <div className="axa-timeline">
            <AxTimelineRow
              tone="ok"
              title="Current structure"
              sub={"Gross " + CUR + " " + money(p.gross) + " · net " + CUR + " " + money(p.net)}
              when={fmtDate(draft.payFrom)}
            />
            {(draft.history || [])
              .filter((h) => /salary|pay/i.test(h.t))
              .map((h, i) => (
                <AxTimelineRow key={i} title={h.t} sub={h.s} when={fmtDate(h.d)} />
              ))}
          </div>
        </div>

        {/* ---------------------------------------------------- SALES ROLE */}
        <div className={"axa-pane" + (tab === "sales" ? " show" : "")}>
          <AxSwitchRow
            label="Assign as salesman"
            note="Makes this person selectable on sales invoices, quotations and delivery notes in the branches you list below."
            checked={!!draft.isSalesman}
            onChange={(e) => set("isSalesman", e.target.checked)}
          />
          {draft.isSalesman ? (
            <>
              <AxSectionHead>Sales identity</AxSectionHead>
              <div className="axa-formgrid">
                <Txt f="smCode" label="Salesman code" req className="mono" />
                <Sel f="smType" label="Salesman type" req list={["Counter", "Van sales", "Field sales", "Tele sales"]} />
                <Sel f="custMode" label="Customers" list={["All customers", "Assigned only", "By territory"]} />
                <Sel f="priceList" label="Default price list" list={lk.priceLists} />
                <AxField label="Max discount %" hint="Anything above this needs approval at the counter.">
                  {num("maxDisc", { min: 0, max: 100, step: 0.5 })}
                </AxField>
                <Sel f="commScheme" label="Commission scheme" list={lk.commissionSchemes} placeholder="None" />
              </div>

              <AxSectionHead>Sells from</AxSectionHead>
              <ChipBox
                which="sell"
                values={draft.sellFrom || []}
                addLabel="+ Add branch"
                onAdd={(gd) => patch({ sellFrom: [...(draft.sellFrom || []), gd] })}
                onRemove={(gd) => patch({ sellFrom: draft.sellFrom.filter((x) => x !== gd) })}
              />

              <AxSectionHead>
                Targets
                <button
                  type="button"
                  className="axe-hintbtn"
                  onClick={() => {
                    const d = today();
                    addRow("targets", {
                      guid: "",
                      period: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()] + " " + d.getFullYear(),
                      ptype: "Month",
                      measure: "Net sales",
                      target: 0,
                      achieved: 0,
                    });
                  }}
                >
                  + Add target
                </button>
              </AxSectionHead>
              <div className="axe-gscroll">
                <table className="axe-gtable">
                  <thead>
                    <tr>
                      <th style={{ width: 100 }}>Period</th>
                      <th style={{ width: 118 }}>Period type</th>
                      <th style={{ width: 140 }}>Measure</th>
                      <th className="num" style={{ width: 110 }}>
                        Target
                      </th>
                      <th className="num" style={{ width: 110 }}>
                        Achieved
                      </th>
                      <th style={{ width: 150 }}>Attainment</th>
                      <th style={{ width: 34 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {(draft.targets || []).length ? (
                      draft.targets.map((t, i) => {
                        const at = pct(t.achieved, t.target);
                        const cls = at >= 100 ? "" : at >= 80 ? "mid" : "bad";
                        const isCount = /count|customers/i.test(t.measure);
                        return (
                          <tr key={t.guid || "n" + i}>
                            <td>{cellIn("targets", i, "period")}</td>
                            <td>{cellSel("targets", i, "ptype", ["Month", "Quarter", "Year"])}</td>
                            <td>{cellSel("targets", i, "measure", lk.targetMeasures)}</td>
                            <td className="num">{cellIn("targets", i, "target", { type: "number", className: "num mono" })}</td>
                            <td className="num mono">{isCount ? t.achieved : money(t.achieved)}</td>
                            <td>
                              <div className="axe-attain">
                                <span className={"pc " + cls}>{Math.round(at)}%</span>
                                <span className={"axe-meter " + cls}>
                                  <i style={{ width: Math.min(100, at).toFixed(1) + "%" }} />
                                </span>
                              </div>
                            </td>
                            <td>
                              <RowDel list="targets" i={i} />
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} className="axe-emptycell">
                          No target set. A salesman without a target never appears on the performance report.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {(() => {
                const sales = (draft.targets || []).find((t) => t.measure === "Net sales");
                const sp = sales ? pct(sales.achieved, sales.target) : 0;
                return (
                  <div className="axa-facts" style={{ marginTop: 13 }}>
                    <Fact l="Targets set" v={(draft.targets || []).length} s="this period" />
                    <Fact
                      l="Sales attainment"
                      v={sales ? Math.round(sp) + "%" : "—"}
                      s={sales ? money(sales.achieved) + " / " + money(sales.target) : "no sales target"}
                      tone={sales ? (sp >= 100 ? "good" : sp >= 80 ? "warn" : "bad") : ""}
                    />
                    <Fact l="Commission scheme" v={draft.commScheme ? "Set" : "None"} s={draft.commScheme || "nothing payable"} />
                    <Fact l="Achieved figures" v="Live" s="from posted sales, by salesman code" />
                  </div>
                );
              })()}
            </>
          ) : null}
        </div>

        {/* ---------------------------------------------------- LEAVE */}
        <div className={"axa-pane" + (tab === "leave" ? " show" : "")}>
          <AxSectionHead>Policy</AxSectionHead>
          <div className="axa-formgrid">
            <Sel f="leavePolicy" label="Leave policy" list={lk.leavePolicies} />
            <AxField label="Annual leave (days)" hint={svc >= 5 ? "4 weeks statutory after 5 years" : "3 weeks statutory after 1 year"}>
              {num("annualLeave")}
            </AxField>
            <Sel f="ticket" label="Air ticket" list={["None", "Annual — economy", "Every 2 years — economy", "Annual — business"]} />
            <Sel f="shift" label="Shift" list={lk.shifts} />
            <Sel f="weekOff" label="Weekly off" list={["Friday", "Friday & Saturday", "Saturday", "Rotational"]} />
            <Txt f="bioId" label="Biometric / attendance ID" className="mono" placeholder="Device enrolment no." />
          </div>

          <AxSectionHead>Balances this year</AxSectionHead>
          <table className="axe-gtable">
            <thead>
              <tr>
                <th>Leave type</th>
                <th className="num" style={{ width: 100 }}>
                  Opening
                </th>
                <th className="num" style={{ width: 100 }}>
                  Accrued
                </th>
                <th className="num" style={{ width: 100 }}>
                  Taken
                </th>
                <th className="num" style={{ width: 110 }}>
                  Balance
                </th>
              </tr>
            </thead>
            <tbody>
              {(draft.leaves || []).map((l, i) => {
                const bal = +l.opening + +l.accrued - +l.taken;
                return (
                  <tr key={l.t}>
                    <td>{l.t}</td>
                    <td className="num">{cellIn("leaves", i, "opening", { type: "number", className: "num mono" })}</td>
                    <td className="num mono">{l.accrued}</td>
                    <td className="num mono">{l.taken}</td>
                    <td className={"num mono axe-strong" + (bal < 0 ? " axe-badtext" : "")}>{bal}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="axe-note">Opening is editable for the first year only; accrued and taken come from the leave module.</div>

          <AxSectionHead>Notice period</AxSectionHead>
          <div className="axa-facts">
            <Fact l="Notice period" v={svc >= 2 ? "2 months" : "1 month"} s="statutory, by service length" />
            <Fact l="Annual entitlement" v={(draft.annualLeave || 21) + " days"} s={svc >= 5 ? "4 weeks after 5 years" : "3 weeks after 1 year"} />
            <Fact l="Air ticket" v={draft.ticket || "None"} s="home-country sector" />
            <Fact l="Weekly off" v={draft.weekOff || "—"} s={draft.shift || ""} />
          </div>
        </div>

        {/* ---------------------------------------------------- ASSETS */}
        <div className={"axa-pane" + (tab === "assets" ? " show" : "")}>
          <AxSectionHead>
            Company property issued
            <button
              type="button"
              className="axe-hintbtn"
              onClick={() => addRow("assets", { guid: "", t: lk.assetTypes[0], sn: "", on: iso(today()), cond: "New", back: "" })}
            >
              + Issue asset
            </button>
          </AxSectionHead>
          {(draft.assets || []).length ? (
            <div className="axe-gscroll">
              <table className="axe-gtable">
                <thead>
                  <tr>
                    <th style={{ width: 170 }}>Asset</th>
                    <th style={{ width: 150 }}>Serial / IMEI</th>
                    <th style={{ width: 138 }}>Issued</th>
                    <th style={{ width: 110 }}>Condition</th>
                    <th style={{ width: 150 }}>Returned</th>
                    <th style={{ width: 34 }} />
                  </tr>
                </thead>
                <tbody>
                  {draft.assets.map((a, i) => (
                    <tr key={a.guid || "n" + i}>
                      <td>{cellSel("assets", i, "t", lk.assetTypes)}</td>
                      <td>{cellIn("assets", i, "sn", { className: "mono" })}</td>
                      <td>{cellIn("assets", i, "on", { type: "date" })}</td>
                      <td>{cellSel("assets", i, "cond", ["New", "Good", "Fair", "Damaged"])}</td>
                      <td>
                        <div className="axe-cellstack">
                          {cellIn("assets", i, "back", { type: "date" })}
                          {!a.back ? <span className="axe-st warning">With employee</span> : null}
                        </div>
                      </td>
                      <td>
                        <RowDel list="assets" i={i} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="axa-emptyline">Nothing issued. Assets outstanding block the exit clearance.</div>
          )}

          <AxSectionHead>ERP access</AxSectionHead>
          <div className="axa-formgrid">
            <AxField label="Linked ERP user" hint={draft.userName ? "Linked to " + draft.userName : "Name, code, branches and email are carried across."}>
              <div className="axe-withbtn">
                <input type="text" className="mono" value={draft.userName} placeholder="Not linked" readOnly />
                <button type="button" className="axa-btn axa-btn-ghost" onClick={createUser} disabled={!!draft.userName}>
                  Create user
                </button>
              </div>
            </AxField>
            <AxSwitchRow label="POS / counter login" checked={!!draft.posLogin} onChange={(e) => set("posLogin", e.target.checked)} />
            <Sel f="till" label="Till assignment" list={lk.tills} placeholder="—" />
          </div>

          <AxSectionHead>Approval limits</AxSectionHead>
          <div className="axa-formgrid">
            <AxField label="Discount approval up to (%)">{num("limDisc")}</AxField>
            <AxField label={"Credit note approval up to (" + CUR + ")"}>{num("limCn")}</AxField>
            <AxField label={"Purchase order approval up to (" + CUR + ")"}>{num("limPo")}</AxField>
          </div>
        </div>

        {/* ---------------------------------------------------- ACTIVITY */}
        <div className={"axa-pane" + (tab === "log" ? " show" : "")}>
          <AxSectionHead>At a glance</AxSectionHead>
          <div className="axa-facts">
            <AxFact k="Created" v={fmtDate(draft.createdOn)} s={draft.createdBy ? "by " + draft.createdBy : ""} />
            <AxFact
              k="Documents"
              v={(draft.docs || []).length}
              s={(draft.docs || []).reduce((a, d) => a + (d.files || []).length, 0) + " scans"}
            />
            <AxFact k="ERP user" v={draft.userName ? "Linked" : "Not linked"} />
            <AxFact k="Assets out" v={(draft.assets || []).filter((a) => !a.back).length} s="returned on exit" />
          </div>

          <AxSectionHead>Change history</AxSectionHead>
          <div className="axa-timeline">
            {(draft.history || []).length ? (
              draft.history.map((h, i) => (
                <AxTimelineRow key={i} tone={i ? "" : "ok"} title={h.t} sub={[h.s, h.by && "by " + h.by].filter(Boolean).join(" · ")} when={fmtDate(h.d)} />
              ))
            ) : (
              <div className="axa-emptyline">
                {isNew ? "The history starts when you save this person." : "No changes recorded for this person yet."}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============ FOOTER ============ */}
      <div className="axa-dfoot">
        <span className={"msg" + (msg ? " err" : "")}>{msg}</span>
        <span className="sp" />
        {!isNew && canDelete ? (
          <button type="button" className="axa-btn axa-btn-danger" onClick={() => onDelete && onDelete(draft)}>
            Delete
          </button>
        ) : null}
        <button type="button" className="axa-btn axa-btn-ghost" onClick={reset} disabled={!dirty && !isNew}>
          Reset
        </button>
        <button type="button" className="axa-btn axa-btn-ghost" onClick={onClose}>
          Close
        </button>
        {canSave ? (
          <button type="submit" className="axa-btn axa-btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Save"} <span className="axe-kbdhint">F9</span>
          </button>
        ) : null}
      </div>
    </form>
    </Ctx.Provider>
  );
};

export default EmployeeDetail;
